import React from "react";
import Chart from "react-apexcharts";
import type { ApexOptions } from "apexcharts";
import { ChannelCount } from "./chatAnalyticsService";
import { GRID_COLOR, getChannelColor, renderTooltipCard, renderTooltipHeader } from "./chatAnalyticsPalette";

interface ChatChannelVolumeChartProps {
  channels: ChannelCount[];
}

const ChatChannelVolumeChart: React.FC<ChatChannelVolumeChartProps> = ({ channels }) => {
  if (channels.length === 0) {
    return (
      <div className="d-flex align-items-center justify-content-center" style={{ height: "300px" }}>
        <div className="text-center">
          <i className="ti ti-antenna-bars-off fs-1 text-muted mb-3" />
          <p className="text-muted mb-0">No channel data for the selected filters</p>
        </div>
      </div>
    );
  }

  const categories = channels.map((c) => c.external_chat_channel);
  const colors = channels.map((c) => getChannelColor(c.external_chat_channel));

  const options: ApexOptions = {
    chart: { type: "bar", height: 300, toolbar: { show: false }, animations: { enabled: false } },
    colors,
    plotOptions: {
      bar: {
        distributed: true,
        borderRadius: 6,
        borderRadiusApplication: "end",
        columnWidth: "50%",
      },
    },
    fill: {
      type: "gradient",
      gradient: { shade: "light", type: "vertical", shadeIntensity: 0.25, opacityFrom: 1, opacityTo: 0.88 },
    },
    dataLabels: {
      enabled: true,
      style: { fontWeight: 700, colors: ["#171123"] },
      offsetY: -20,
    },
    stroke: { width: 0 },
    grid: { borderColor: GRID_COLOR, strokeDashArray: 4, xaxis: { lines: { show: false } } },
    xaxis: {
      categories,
      axisBorder: { show: false },
      axisTicks: { show: false },
      labels: { style: { fontWeight: 600 } },
    },
    yaxis: { min: 0, labels: { formatter: (val: number) => Math.round(val).toString() } },
    legend: { show: false },
    tooltip: {
      custom: ({ dataPointIndex }) => {
        const channel = channels[dataPointIndex];
        const swatch = getChannelColor(channel.external_chat_channel);
        return renderTooltipCard(
          renderTooltipHeader(channel.external_chat_channel, channel.total_chats, swatch)
        );
      },
    },
    states: { hover: { filter: { type: "darken" } } },
  };

  return <Chart options={options} series={[{ name: "Chats", data: channels.map((c) => c.total_chats) }]} type="bar" height={300} />;
};

export default ChatChannelVolumeChart;
